/**
 * XRMarker3DOverlay — v10.12.0
 * Overlay 3D fullscreen que renderiza modelos GLB anclados al marcador
 * detectado por la cámara, con escala derivada del bounding box y
 * calibración persistida (offset/scale/rotation/opacity).
 *
 * Reglas críticas:
 *  - El canvas se dimensiona con `containerWidth * dpr` (backing store)
 *    pero se estira con CSS a `100%` del contenedor (evita blur).
 *  - Todas las coordenadas se guardan NORMALIZADAS (0..1). Ninguna
 *    posición es absoluta en píxeles.
 *  - El overlay compensa `object-fit: cover` del video: recalcula la
 *    zona visible del video dentro del contenedor y mapea los bounds
 *    del marker al espacio pantalla.
 *  - Escala del modelo: proporcional al ancho del bbox del marker
 *    (bboxNormalizedWidth * containerWidth * calibScale).
 *  - `ResizeObserver` recalcula viewport/renderer al cambio de tamaño
 *    o rotación de pantalla.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Cube } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Calcula el rect visible del video dentro del contenedor
 * asumiendo `object-fit: cover`. Retorna coordenadas de contenedor
 * (px) y el factor de recorte necesario para mapear coords
 * normalizadas del video → coords normalizadas del contenedor. */
function computeCoverRect(containerW, containerH, videoW, videoH) {
  if (!videoW || !videoH || !containerW || !containerH) {
    return { visibleX: 0, visibleY: 0, visibleW: containerW, visibleH: containerH, scaleX: 1, scaleY: 1 };
  }
  const containerRatio = containerW / containerH;
  const videoRatio = videoW / videoH;
  let scale, drawnW, drawnH;
  if (videoRatio > containerRatio) {
    // Video más ancho → recorta a los lados
    scale = containerH / videoH;
    drawnW = videoW * scale;
    drawnH = containerH;
  } else {
    scale = containerW / videoW;
    drawnW = containerW;
    drawnH = videoH * scale;
  }
  const visibleX = (containerW - drawnW) / 2;
  const visibleY = (containerH - drawnH) / 2;
  return { visibleX, visibleY, visibleW: drawnW, visibleH: drawnH, scaleX: scale, scaleY: scale };
}

async function fetchAssetBlob(assetId, token) {
  const res = await fetch(`${BACKEND_URL}/api/xr/assets/${assetId}/file`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`asset ${res.status}`);
  return await res.blob();
}

export default function XRMarker3DOverlay({
  markers,
  activeMarker,        // marker_id detectado
  bounds,              // {x, y, w, h, cx, cy} normalizados en el video
  videoFrame,          // {videoWidth, videoHeight} del <video>
  calibration,         // {offset_x, offset_y, scale, rotation_deg, opacity}
  accessToken,
  debug = false,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const stateRef = useRef({ scene: null, renderer: null, camera: null, model: null, activeAssetId: null, dpr: 1, raf: null });
  const [viewport, setViewport] = useState({ w: 0, h: 0 });
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');

  const bindings = useMemo(
    () => (markers || []).filter((m) => m.asset_id).reduce((acc, m) => { acc[m.marker_id] = m; return acc; }, {}),
    [markers]
  );

  /* ---------- Init Three.js + ResizeObserver ---------- */
  useEffect(() => {
    let alive = true;
    let ro;
    (async () => {
      try {
        const THREE = await import('three');
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2); // limita en móviles antiguos
        const scene = new THREE.Scene();
        // Cámara ortográfica para trabajar en coords de pantalla (píxeles CSS)
        const camera = new THREE.OrthographicCamera(0, 1, 1, 0, -1000, 1000);
        const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'high-performance' });
        renderer.setPixelRatio(dpr);
        renderer.setClearColor(0x000000, 0);
        scene.add(new THREE.HemisphereLight(0xffffff, 0x1e293b, 1.4));
        const dir = new THREE.DirectionalLight(0xffffff, 0.9);
        dir.position.set(0.5, 1, 1);
        scene.add(dir);
        stateRef.current = { THREE, scene, camera, renderer, model: null, activeAssetId: null, dpr, raf: null };

        const applyResize = () => {
          const w = container.clientWidth;
          const h = container.clientHeight;
          renderer.setSize(w, h, false);
          // Ortho camera en unidades píxel del contenedor
          camera.left = 0; camera.right = w; camera.top = h; camera.bottom = 0;
          camera.updateProjectionMatrix();
          setViewport({ w, h });
        };
        ro = new ResizeObserver(applyResize);
        ro.observe(container);
        applyResize();

        const animate = () => {
          const st = stateRef.current;
          if (!st.renderer) return;
          st.renderer.render(st.scene, st.camera);
          st.raf = requestAnimationFrame(animate);
        };
        stateRef.current.raf = requestAnimationFrame(animate);
        if (alive) setReady(true);
      } catch (e) {
        setError(`Init: ${e.message}`);
      }
    })();
    return () => {
      alive = false;
      if (ro) ro.disconnect();
      const st = stateRef.current;
      if (st.raf) cancelAnimationFrame(st.raf);
      try { st.renderer?.dispose?.(); } catch { /* noop */ }
      stateRef.current = { scene: null, renderer: null, camera: null, model: null, activeAssetId: null, dpr: 1, raf: null };
    };
  }, []);

  /* ---------- Carga del modelo cuando cambia el activeMarker ---------- */
  useEffect(() => {
    if (!ready || !activeMarker) return;
    const binding = bindings[activeMarker];
    if (!binding || !binding.asset_id) return;
    if (stateRef.current.activeAssetId === binding.asset_id) return; // ya cargado
    let cancelled = false;
    (async () => {
      try {
        const st = stateRef.current;
        const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
        const blob = await fetchAssetBlob(binding.asset_id, accessToken);
        const url = URL.createObjectURL(blob);
        const loader = new GLTFLoader();
        loader.load(url, (gltf) => {
          if (cancelled) { URL.revokeObjectURL(url); return; }
          if (st.model) { st.scene.remove(st.model); }
          const model = gltf.scene;
          // Normalizamos el modelo a un cubo unitario centrado en el origen
          const THREE = st.THREE;
          const box = new THREE.Box3().setFromObject(model);
          const size = box.getSize(new THREE.Vector3()).length() || 1;
          const center = box.getCenter(new THREE.Vector3());
          model.position.sub(center);
          model.scale.setScalar(1 / size);
          const holder = new THREE.Group();
          holder.add(model);
          st.scene.add(holder);
          st.model = holder;
          st.activeAssetId = binding.asset_id;
          URL.revokeObjectURL(url);
        }, undefined, (e) => {
          if (!cancelled) setError(`Modelo: ${e?.message || e}`);
        });
      } catch (e) {
        if (!cancelled) setError(`Modelo: ${e.message}`);
      }
    })();
    return () => { cancelled = true; };
  }, [ready, activeMarker, bindings, accessToken]);

  /* ---------- Posicionamiento cada frame: síncrono con props ---------- */
  useEffect(() => {
    const st = stateRef.current;
    if (!st.model || !viewport.w || !viewport.h) return;
    if (st.model) st.model.visible = false;

    // Necesitamos bbox del marker + tamaño del video.
    if (!bounds || !videoFrame?.videoWidth) return;

    // 1) Calcular rect visible del video en el contenedor (compensa cover)
    const cover = computeCoverRect(viewport.w, viewport.h, videoFrame.videoWidth, videoFrame.videoHeight);

    // 2) Mapear bounds normalizados del video → coordenadas del contenedor (px CSS)
    //    (bounds.cx, cy son 0..1 del frame de video crudo)
    const px = cover.visibleX + bounds.cx * cover.visibleW;
    const py = cover.visibleY + bounds.cy * cover.visibleH;
    // Y en ortho: 0 abajo → invertimos: py' = h - py
    const orthoY = viewport.h - py;

    // 3) Escala del overlay = ancho del bbox del marker en px * factor de calibración
    const markerPxW = bounds.w * cover.visibleW; // ancho aparente del marker
    const cal = calibration || {};
    const calScale = Math.max(0.1, Math.min(5.0, cal.scale || 1));
    const overlayPx = Math.max(20, markerPxW * 2.0 * calScale); // 2x el tamaño del marker por default

    // 4) Offset normalizado adicional (calibración): en unidades de bbox
    const offX = (cal.offset_x || 0) * markerPxW;
    const offY = (cal.offset_y || 0) * markerPxW;

    // 5) Clamp al viewport (evita que salga completamente)
    const half = overlayPx / 2;
    const clampedX = Math.max(half, Math.min(viewport.w - half, px + offX));
    const clampedY = Math.max(half, Math.min(viewport.h - half, orthoY - offY));

    // 6) Aplicar transform al holder
    st.model.visible = true;
    st.model.position.set(clampedX, clampedY, 0);
    st.model.scale.setScalar(overlayPx);
    st.model.rotation.z = ((cal.rotation_deg || 0) * Math.PI) / 180;
    // Opacity — recorremos meshes y les setteamos opacity si sus materiales lo permiten
    const op = cal.opacity != null ? cal.opacity : 1;
    st.model.traverse((n) => {
      if (n.isMesh && n.material) {
        const materials = Array.isArray(n.material) ? n.material : [n.material];
        materials.forEach((m) => { m.transparent = op < 1; m.opacity = op; });
      }
    });
  }, [bounds, videoFrame, viewport, calibration]);

  const hasBinding = activeMarker && bindings[activeMarker];

  return (
    <div ref={containerRef} className="absolute inset-0 pointer-events-none" data-testid="xr-3d-overlay">
      <canvas ref={canvasRef} className="block w-full h-full" style={{ width: '100%', height: '100%' }} />

      {hasBinding && (
        <div className="absolute top-3 left-3 pointer-events-none px-2 py-0.5 rounded-full bg-cyan-500/90 backdrop-blur text-slate-900 text-[10px] font-bold flex items-center gap-1 shadow-lg" data-testid="xr-3d-overlay-badge">
          <Cube size={10} weight="fill" /> {bindings[activeMarker].label || activeMarker}
        </div>
      )}

      {debug && (
        <div className="absolute top-3 right-3 pointer-events-none px-2 py-1.5 rounded bg-black/70 text-emerald-300 font-mono text-[9px] leading-tight max-w-[42vw]" data-testid="xr-debug-hud">
          <div>vp: {viewport.w}×{viewport.h}</div>
          <div>video: {videoFrame?.videoWidth || '—'}×{videoFrame?.videoHeight || '—'}</div>
          <div>dpr: {stateRef.current?.dpr?.toFixed?.(2) || 1}</div>
          <div>marker: {activeMarker || '—'}</div>
          {bounds && <div>bbox: {(bounds.w * 100).toFixed(0)}% @ ({(bounds.cx * 100).toFixed(0)},{(bounds.cy * 100).toFixed(0)})</div>}
          {calibration && <div>cal: s={calibration.scale?.toFixed(2)} r={calibration.rotation_deg?.toFixed(0)}° op={calibration.opacity?.toFixed(2)}</div>}
        </div>
      )}

      {error && (
        <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded bg-red-500/80 text-white text-[10px] pointer-events-auto" data-testid="xr-3d-overlay-error">
          {error}
        </div>
      )}
    </div>
  );
}
